/* ==========================================================================
   Biểu đồ trong câu trả lời: khối ```chart {JSON}``` → SVG.

   An toàn: chỉ nhận JSON (không HTML, không script); mọi chữ đi qua escape,
   mọi số qua Number(). Không thư viện ngoài.
   Thiết kế theo skill dataviz: bảng màu phân loại đã kiểm định (thứ tự cố
   định, bản tối riêng), một trục, cột ≤ 24px bo 4px đầu dữ liệu, đường 2px,
   điểm r=4 có vòng 2px màu nền, lưới 1px nhạt; chú giải khi ≥ 2 chuỗi;
   tooltip <title> trên từng mark; luôn kèm "Xem bảng số" (ba màu sáng dưới
   3:1 trên nền sáng ⇒ bắt buộc có bảng/nhãn).

   Spec:
     { "type": "bar" | "hbar" | "line" | "stacked" | "pie",
       "title": "…", "unit": "t", "labels": ["…"],
       "series": [{ "name": "…", "data": [số…] }],
       "source": "…", "note": "…" }
   ========================================================================== */

const esc = (s: string): string =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export interface ChartSeries {
  name: string;
  data: number[];
}
export interface ChartSpec {
  type: "bar" | "hbar" | "line" | "stacked" | "pie";
  title?: string;
  unit?: string;
  labels: string[];
  series: ChartSeries[];
  source?: string;
  note?: string;
}

const TYPES = ["bar", "hbar", "line", "stacked", "pie"];
const MAX_SERIES = 6;
const MAX_LABELS = 40;
const MAX_SLICES = 8;

/** Chuẩn hoá spec do server/mô hình gửi; trả null nếu không dùng được. */
export function parseChart(raw: string): ChartSpec | null {
  let o: unknown;
  try {
    o = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!o || typeof o !== "object") return null;
  const r = o as Record<string, unknown>;
  const type = TYPES.includes(String(r.type)) ? (String(r.type) as ChartSpec["type"]) : "bar";
  const labels = Array.isArray(r.labels) ? r.labels.slice(0, MAX_LABELS).map((x) => String(x ?? "").slice(0, 60)) : [];
  const series = (Array.isArray(r.series) ? r.series : [])
    .filter((s): s is Record<string, unknown> => !!s && typeof s === "object")
    .slice(0, MAX_SERIES)
    .map((s, i) => ({
      name: String(s.name ?? "Chuỗi " + (i + 1)).slice(0, 60),
      data: labels.map((_l, j) => {
        const v = Array.isArray(s.data) ? Number(s.data[j]) : NaN;
        return Number.isFinite(v) ? v : 0;
      }),
    }));
  if (!labels.length || !series.length) return null;
  const str = (k: string, n = 160) => (typeof r[k] === "string" ? String(r[k]).slice(0, n) : undefined);
  return { type, title: str("title"), unit: str("unit", 20), labels, series, source: str("source"), note: str("note", 240) };
}

/* số kiểu Việt Nam, rút gọn từ nghìn trở lên cho nhãn trục */
function fmt(v: number, compact = false): string {
  const a = Math.abs(v);
  if (compact && a >= 1e9) return trim(v / 1e9) + " tỷ";
  if (compact && a >= 1e6) return trim(v / 1e6) + " tr";
  if (compact && a >= 1e4) return trim(v / 1e3) + "K";
  /* giữ nguyên số liệu tới 2 chữ số thập phân (khớp Excel); bỏ số 0 thừa */
  const [i, f] = (Math.round(a * 100) / 100).toFixed(2).split(".");
  const frac = (f as string).replace(/0+$/, "");
  return (v < 0 ? "−" : "") + (i as string).replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (frac ? "," + frac : "");
}
function trim(v: number): string {
  return fmt(Math.round(v * 10) / 10);
}

function niceStep(range: number, ticks = 4): number {
  if (range <= 0) return 1;
  const raw = range / ticks;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
}

const W = 360;
const color = (i: number) => "var(--apx-series-" + ((i % MAX_SERIES) + 1) + ")";

/** Path cột: bo 4px ở đầu dữ liệu, vuông ở đường gốc. */
function colPath(x0: number, y0: number, w0: number, h0: number, up: boolean): string {
  const q = (n: number) => Math.round(n * 10) / 10;
  const x = q(x0), y = q(y0), w = q(w0), h = q(h0);
  const r = Math.min(4, w / 2, h);
  if (h <= 0.5) return "";
  if (up)
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  return `M${x},${y}V${y + h - r}Q${x},${y + h} ${x + r},${y + h}H${x + w - r}Q${x + w},${y + h} ${x + w},${y + h - r}V${y}Z`;
}
function barPathH(x0: number, y0: number, w0: number, h: number): string {
  const q = (n: number) => Math.round(n * 10) / 10;
  const x = q(x0), y = q(y0), w = q(w0);
  const r = Math.min(4, h / 2, w);
  if (w <= 0.5) return "";
  return `M${x},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h - r}Q${x + w},${y + h} ${x + w - r},${y + h}H${x}Z`;
}

const tip = (label: string, s: string, v: number, unit?: string) =>
  `<title>${esc(label)}${s ? " · " + esc(s) : ""}: ${esc(fmt(v))}${unit ? " " + esc(unit) : ""}</title>`;

function legend(spec: ChartSpec, names?: string[]): string {
  const list = names || spec.series.map((s) => s.name);
  if (list.length < 2) return "";
  return `<div class="apx-legend">${list.map((n, i) => `<span><i style="background:${color(i)}"></i>${esc(n)}</span>`).join("")}</div>`;
}

function table(spec: ChartSpec): string {
  const head = `<tr><th></th>${spec.series.map((s) => `<th class="apx-right">${esc(s.name)}</th>`).join("")}</tr>`;
  const rows = spec.labels
    .map((l, j) => `<tr><td>${esc(l)}</td>${spec.series.map((s) => `<td class="apx-right">${esc(fmt(s.data[j] ?? 0))}</td>`).join("")}</tr>`)
    .join("");
  return `<details class="apx-chart-data"><summary>Xem bảng số${spec.unit ? " (" + esc(spec.unit) + ")" : ""}</summary><div class="apx-tw"><table class="apx-table"><thead>${head}</thead><tbody>${rows}</tbody></table></div></details>`;
}

/* ---------------- cột dọc, cột chồng, đường ---------------- */
function xy(spec: ChartSpec): string {
  const n = spec.labels.length;
  const stacked = spec.type === "stacked";
  const line = spec.type === "line";
  const vals = stacked
    ? spec.labels.map((_l, j) => spec.series.reduce((a, s) => a + Math.max(0, s.data[j] ?? 0), 0))
    : spec.series.flatMap((s) => s.data);
  let lo = Math.min(0, ...vals);
  let hi = Math.max(0, ...vals);
  if (line) {
    const mn = Math.min(...vals);
    const mx = Math.max(...vals);
    /* đường: không ép về 0 khi dữ liệu nằm xa 0 (xu hướng mới là điều cần đọc) */
    if (mn > 0 && mn > (mx - mn) * 1.5) lo = mn - (mx - mn) * 0.15;
    hi = mx;
  }
  const step = niceStep(hi - lo);
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step || step;
  const ML = 44, MR = 8, MT = 10, MB = n > 8 ? 46 : 28, H = 190;
  const pw = W - ML - MR, ph = H - MT - MB;
  const y = (v: number) => MT + ph - ((v - lo) / (hi - lo)) * ph;
  const band = pw / n;
  const cx = (j: number) => ML + band * j + band / 2;
  let g = "";
  for (let v = lo; v <= hi + step / 2; v += step) {
    g += `<line class="apx-grid" x1="${ML}" x2="${W - MR}" y1="${y(v).toFixed(1)}" y2="${y(v).toFixed(1)}"/><text class="apx-tick" x="${ML - 6}" y="${(y(v) + 3.5).toFixed(1)}" text-anchor="end">${esc(fmt(v, true))}</text>`;
  }
  const every = Math.ceil(n / (n > 8 ? 12 : 8));
  spec.labels.forEach((l, j) => {
    if (j % every) return;
    const t = l.length > 12 ? l.slice(0, 11) + "…" : l;
    g += n > 8
      ? `<text class="apx-tick" transform="translate(${cx(j).toFixed(1)},${H - MB + 10}) rotate(-40)" text-anchor="end">${esc(t)}</text>`
      : `<text class="apx-tick" x="${cx(j).toFixed(1)}" y="${H - MB + 14}" text-anchor="middle">${esc(t)}</text>`;
  });
  let marks = "";
  if (line) {
    spec.series.forEach((s, i) => {
      const pts = s.data.map((v, j) => `${cx(j).toFixed(1)},${y(v).toFixed(1)}`);
      marks += `<polyline fill="none" stroke="${color(i)}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" points="${pts.join(" ")}"/>`;
      s.data.forEach((v, j) => {
        marks += `<circle class="apx-dot" cx="${cx(j).toFixed(1)}" cy="${y(v).toFixed(1)}" r="${n > 16 ? 3 : 4}" fill="${color(i)}">${tip(spec.labels[j] as string, spec.series.length > 1 ? s.name : "", v, spec.unit)}</circle>`;
      });
    });
    /* nhãn trực tiếp: chỉ giá trị cuối của mỗi chuỗi */
    spec.series.forEach((s) => {
      const v = s.data[n - 1] ?? 0;
      marks += `<text class="apx-val" x="${(cx(n - 1) - 4).toFixed(1)}" y="${(y(v) - 8).toFixed(1)}" text-anchor="end">${esc(fmt(v))}</text>`;
    });
  } else if (stacked) {
    const bw = Math.min(24, band * 0.6);
    spec.labels.forEach((l, j) => {
      let base = 0;
      spec.series.forEach((s, i) => {
        const v = Math.max(0, s.data[j] ?? 0);
        if (!v) return;
        const top = y(base + v);
        const h = y(base) - top - (base > 0 ? 2 : 0); /* khe 2px màu nền giữa các đoạn */
        const isTop = spec.series.slice(i + 1).every((t) => !(t.data[j] ?? 0));
        const x = cx(j) - bw / 2;
        marks += isTop
          ? `<path fill="${color(i)}" d="${colPath(x, top, bw, h, true)}">${tip(l, s.name, v, spec.unit)}</path>`
          : `<rect fill="${color(i)}" x="${x.toFixed(1)}" y="${top.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, h).toFixed(1)}">${tip(l, s.name, v, spec.unit)}</rect>`;
        base += v;
      });
    });
  } else {
    const k = spec.series.length;
    const bw = Math.min(24, (band * 0.7 - (k - 1) * 2) / k);
    spec.series.forEach((s, i) => {
      s.data.forEach((v, j) => {
        const x = cx(j) - (k * bw + (k - 1) * 2) / 2 + i * (bw + 2);
        const up = v >= 0;
        const top = up ? y(v) : y(0);
        const h = Math.abs(y(v) - y(0));
        marks += `<path fill="${color(i)}" d="${colPath(x, top, bw, h, up)}">${tip(spec.labels[j] as string, k > 1 ? s.name : "", v, spec.unit)}</path>`;
      });
    });
    /* nhãn trực tiếp: chỉ cột lớn nhất (một chuỗi) */
    if (k === 1) {
      const d = spec.series[0]!.data;
      const j = d.indexOf(Math.max(...d));
      marks += `<text class="apx-val" x="${cx(j).toFixed(1)}" y="${(y(d[j] as number) - 5).toFixed(1)}" text-anchor="middle">${esc(fmt(d[j] as number))}</text>`;
    }
  }
  const zero = lo < 0 ? `<line class="apx-axis" x1="${ML}" x2="${W - MR}" y1="${y(0).toFixed(1)}" y2="${y(0).toFixed(1)}"/>` : "";
  return `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(spec.title || "Biểu đồ")}">${g}${zero}${marks}</svg>${legend(spec)}`;
}

/* ---------------- cột ngang (nhãn dài: máy, khách, vật tư) ---------------- */
function hbar(spec: ChartSpec): string {
  const s = spec.series[0]!;
  const n = spec.labels.length;
  const row = 22, MT = 4, ML = 118, MR = 52;
  const H = MT + n * row + 4;
  const hi = Math.max(0, ...s.data) || 1;
  const pw = W - ML - MR;
  let out = "";
  spec.labels.forEach((l, j) => {
    const v = s.data[j] ?? 0;
    const yy = MT + j * row;
    const t = l.length > 20 ? l.slice(0, 19) + "…" : l;
    const w = (Math.max(0, v) / hi) * pw;
    out += `<text class="apx-tick" x="${ML - 6}" y="${yy + 14}" text-anchor="end">${esc(t)}</text>`;
    out += `<path fill="${color(0)}" d="${barPathH(ML, yy + 5, w, 13)}">${tip(l, "", v, spec.unit)}</path>`;
    out += `<text class="apx-val" x="${(ML + w + 4).toFixed(1)}" y="${yy + 15}">${esc(fmt(v))}</text>`;
  });
  return `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(spec.title || "Biểu đồ")}">${out}</svg>`;
}

/* ---------------- tròn (cơ cấu) ---------------- */
function pie(spec: ChartSpec): string {
  const s = spec.series[0]!;
  let items = spec.labels.map((l, j) => ({ l, v: Math.max(0, s.data[j] ?? 0) })).filter((x) => x.v > 0);
  items.sort((a, b) => b.v - a.v);
  if (items.length > MAX_SLICES) {
    const rest = items.slice(MAX_SLICES - 1).reduce((a, x) => a + x.v, 0);
    items = [...items.slice(0, MAX_SLICES - 1), { l: "Khác", v: rest }];
  }
  const total = items.reduce((a, x) => a + x.v, 0) || 1;
  const cxp = 90, cyp = 90, R = 78, r0 = 46, H = 180;
  let a0 = -Math.PI / 2, out = "";
  items.forEach((it, i) => {
    const a1 = a0 + (it.v / total) * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (r: number, a: number) => `${(cxp + r * Math.cos(a)).toFixed(2)},${(cyp + r * Math.sin(a)).toFixed(2)}`;
    const d = items.length === 1
      ? `M${cxp - R},${cyp}A${R},${R} 0 1 1 ${cxp + R},${cyp}A${R},${R} 0 1 1 ${cxp - R},${cyp}Z`
      : `M${p(R, a0)}A${R},${R} 0 ${large} 1 ${p(R, a1)}L${p(r0, a1)}A${r0},${r0} 0 ${large} 0 ${p(r0, a0)}Z`;
    out += `<path class="apx-slice" fill="${color(i)}" d="${d}">${tip(it.l, "", it.v, spec.unit)}</path>`;
    a0 = a1;
  });
  out += `<text class="apx-val" x="${cxp}" y="${cyp + 4}" text-anchor="middle">${esc(fmt(total))}${spec.unit ? " " + esc(spec.unit) : ""}</text>`;
  const leg = items
    .map((it, i) => `<text class="apx-tick" x="196" y="${18 + i * 20}"><tspan fill="${color(i)}">■</tspan> ${esc(it.l.length > 18 ? it.l.slice(0, 17) + "…" : it.l)} · ${esc(fmt((it.v / total) * 100))}%</text>`)
    .join("");
  return `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(spec.title || "Biểu đồ")}">${out}${leg}</svg>`;
}

export function renderChart(spec: ChartSpec): string {
  const body = spec.type === "pie" ? pie(spec) : spec.type === "hbar" ? hbar(spec) : xy(spec);
  return (
    `<figure class="apx-chart">` +
    (spec.title ? `<figcaption class="apx-chart-title">${esc(spec.title)}${spec.unit ? ` <span>(${esc(spec.unit)})</span>` : ""}</figcaption>` : "") +
    body +
    (spec.note ? `<p class="apx-chart-note">${esc(spec.note)}</p>` : "") +
    (spec.source ? `<p class="apx-chart-note">Nguồn: ${esc(spec.source)}</p>` : "") +
    table(spec) +
    `</figure>`
  );
}

/** Khối ```chart chưa đóng (đang stream) hoặc JSON hỏng. */
export const CHART_PENDING = `<div class="apx-chart apx-chart-pending">Đang vẽ biểu đồ…</div>`;
